import { AccountClass } from "@common/constants/domains/accounting/accounts";
import { orderedAccountClasses } from "@common/constants/domains/accounting/helpers";
import { asIsSourceCodeText } from "@common/helpers/importedAccountSourceNumber";
import { ImportedAccount } from "@common/schemas/domains/accounting/importedAccount.schema";
import { ImportedAccountGroup } from "@common/schemas/domains/accounting/importedAccountGroup.schema";
import { compareBySortOrderThenName } from "../compareBySortOrderThenName";
import { CoaMappingRow } from "./coaMappingRowTypes";

interface BuildCoaMappingRowsParams {
    accounts: ImportedAccount[];
    groups: ImportedAccountGroup[];
}

function groupByParent<T>(
    items: T[],
    keyOf: (item: T) => string | null,
): Map<string | null, T[]> {
    const byParent = new Map<string | null, T[]>();
    for (const item of items) {
        const list = byParent.get(keyOf(item)) ?? [];
        list.push(item);
        byParent.set(keyOf(item), list);
    }
    return byParent;
}

interface ChildRowsInSourceOrderParams {
    childGroups: ImportedAccountGroup[];
    ownAccounts: ImportedAccount[];
    parentGroup: ImportedAccountGroup | undefined;
    walk: (group: ImportedAccountGroup) => CoaMappingRow[];
}

function childRowsInSourceOrder({
    childGroups,
    ownAccounts,
    parentGroup,
    walk,
}: ChildRowsInSourceOrderParams): CoaMappingRow[] {
    return [
        ...childGroups.map((group) => ({
            sortOrder: group.sortOrder,
            name: group.name,
            rows: walk(group),
        })),
        ...ownAccounts.map((account) => ({
            sortOrder: account.sortOrder,
            name: account.name,
            rows: [accountRow(account, parentGroup)],
        })),
    ]
        .sort(compareBySortOrderThenName)
        .flatMap((entry) => entry.rows);
}

function accountRow(
    account: ImportedAccount,
    parentGroup: ImportedAccountGroup | undefined,
): CoaMappingRow {
    return {
        id: account.id,
        kind: "account",
        name: account.name,
        sourceNumber: asIsSourceCodeText(account),
        account,
        isParentPostingRow:
            parentGroup !== undefined &&
            account.providerId === parentGroup.providerId,
    };
}

export function buildCoaMappingRows({
    accounts,
    groups,
}: BuildCoaMappingRowsParams): CoaMappingRow[] {
    const rows: CoaMappingRow[] = [];

    for (const classification of orderedAccountClasses) {
        const groupsByParentId = groupByParent(
            groups.filter((group) => group.classification === classification),
            (group) => group.parentGroupId,
        );
        const accountsByGroupId = groupByParent(
            accounts.filter(
                (account) => account.classification === classification,
            ),
            (account) => account.importedAccountGroupId,
        );
        const visited = new Set<string>();

        const walk = (group: ImportedAccountGroup): CoaMappingRow[] => {
            if (visited.has(group.id)) {
                return [];
            }
            visited.add(group.id);
            return [
                {
                    id: group.id,
                    kind: "group",
                    name: group.name,
                    sourceNumber: asIsSourceCodeText(group),
                    isParentPostingRow: false,
                    children: childRowsInSourceOrder({
                        childGroups: groupsByParentId.get(group.id) ?? [],
                        ownAccounts: accountsByGroupId.get(group.id) ?? [],
                        parentGroup: group,
                        walk,
                    }),
                },
            ];
        };

        const children = childRowsInSourceOrder({
            childGroups: groupsByParentId.get(null) ?? [],
            ownAccounts: accountsByGroupId.get(null) ?? [],
            parentGroup: undefined,
            walk,
        });
        if (children.length === 0) {
            continue;
        }
        rows.push({
            id: classificationRowId(classification),
            kind: "classification",
            name: classification,
            sourceNumber: null,
            isParentPostingRow: false,
            children,
        });
    }

    return rows;
}

function classificationRowId(classification: AccountClass): string {
    return `classification:${classification}`;
}
